import { useState, useEffect, useCallback } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { MessageCircle, Check, Gift, DollarSign, X } from 'lucide-react'
import { floatHearts } from '../../../lib/animations'

function buildThankYou(giver, item, couple) {
  return `Dear ${giver},\n\nThank you so much for your generous ${item}. Your love and support on our special day meant everything to us.\n\nWith love,\n${couple} ❤️`
}

// ── Thank You Modal ──────────────────────────────────────
function ThankYouModal({ gift, couple, onClose, onSent }) {
  const [msg, setMsg] = useState(buildThankYou(gift.from_name, gift.description, couple))
  const [sending, setSending] = useState(false)
  async function send() {
    setSending(true)
    const btn = document.activeElement
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank')
    floatHearts(btn)
    await supabase.from('gift_log').update({ thank_you_sent:true, thank_you_note:'sent' }).eq('id', gift.id)
    setSending(false); onSent(gift.id); onClose()
  }
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',backdropFilter:'blur(4px)' }}>
      <div className="card fade-up" style={{ width:'100%',maxWidth:420,padding:'1.5rem',position:'relative' }}>
        <button onClick={onClose} style={{ position:'absolute',top:12,right:12,background:'none',border:'none',cursor:'pointer',color:'#BCBCBC' }}><X size={18}/></button>
        <h3 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',margin:'0 0 0.3rem' }}>Send Thank You</h3>
        <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:'0 0 0.875rem' }}>To: <strong>{gift.from_name}</strong> · For: {gift.description}</p>
        <textarea className="input-field" rows={7} value={msg} onChange={e=>setMsg(e.target.value)} style={{ resize:'vertical',fontSize:'0.85rem',lineHeight:1.7 }}/>
        <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',color:'#BCBCBC',margin:'0.3rem 0 0' }}>Auto-generated — edit freely before sending.</p>
        <div style={{ display:'flex',gap:'0.75rem',marginTop:'1.25rem' }}>
          <button className="btn-secondary" onClick={onClose} style={{ flex:1 }}>Cancel</button>
          <button onClick={send} disabled={sending} className="btn-primary" style={{ flex:2,display:'flex',alignItems:'center',justifyContent:'center',gap:'0.4rem' }}>
            <MessageCircle size={14}/> {sending?'Sending…':'Send via WhatsApp ✓'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main ────────────────────────────────────────────────
export default function GiftLogPage() {
  const { wedding }  = useWedding()
  const [gifts,     setGifts]    = useState([])
  const [pending,   setPending]  = useState([])
  const [loading,   setLoading]  = useState(true)
  const [status,    setStatus]   = useState('All')   // All | Pending | Sent
  const [typeF,     setTypeF]    = useState('All')   // All | 🎁 | 💵
  const [thankYou,  setThankYou] = useState(null)

  const couple   = wedding ? `${wedding.partner1_name} & ${wedding.partner2_name}` : ''
  const currency = wedding?.currency === 'USD' ? '$' : '₦'

  const fetch = useCallback(async () => {
    if (!wedding?.id) return
    setLoading(true)
    const [{ data:gl }, { data:reg }, { data:contrib }] = await Promise.all([
      supabase.from('gift_log').select('*').eq('wedding_id', wedding.id).order('created_at',{ascending:false}),
      supabase.from('registry_items').select('*,registry_reservations(id,guest_name,quantity)').eq('wedding_id', wedding.id).in('status',['reserved','available']),
      supabase.from('cash_contributions').select('*').eq('wedding_id', wedding.id).eq('status','success').order('created_at',{ascending:false}),
    ])
    // Merge cash contributions into gift log display (not yet in gift_log table)
    const contribGifts = (contrib||[]).map(c=>({
      id: `contrib_${c.id}`, from_name: c.guest_name||'Anonymous',
      description: 'Cash Contribution', amount: c.amount,
      gift_type:'cash', thank_you_sent:false, received_at: c.created_at?.split('T')[0],
      _isContrib: true
    }))
    const allGifts = [...(gl||[]), ...contribGifts].sort((a,b)=>new Date(b.received_at||b.created_at||0)-new Date(a.received_at||a.created_at||0))
    setGifts(allGifts)
    // Build one pending entry per reservation with quantity
    const pendingList = []
    for (const item of (reg||[])) {
      const reservations = item.registry_reservations||[]
      const loggedNames = (gl||[]).filter(g=>g.registry_item_id===item.id).map(g=>g.from_name)
      for (const res of reservations) {
        if (!loggedNames.includes(res.guest_name)) {
          pendingList.push({ ...item, reservation_id: res.id, reserved_by: res.guest_name, reserved_qty: res.quantity||1 })
        }
      }
    }
    setPending(pendingList)
    setLoading(false)
  }, [wedding?.id])

  useEffect(()=>{ fetch() },[fetch])

  const [marking, setMarking] = useState(null)

  async function markReceived(item) {
    setMarking(item.reservation_id || item.id)
    // Insert into gift_log
    const { error } = await supabase.from('gift_log').insert({
      wedding_id: wedding.id, from_name: item.reserved_by,
      description: item.name, amount: item.price||0,
      gift_type: 'physical', thank_you_sent: false,
      received_at: new Date().toISOString().split('T')[0],
      registry_item_id: item.id,
    })
    if (error) {
      console.error('Gift log insert failed:', error.message)
      // Try without registry_item_id in case column doesn't exist
      await supabase.from('gift_log').insert({
        wedding_id: wedding.id, from_name: item.reserved_by,
        description: item.name, amount: item.price||0,
        gift_type: 'physical', thank_you_sent: false,
        received_at: new Date().toISOString().split('T')[0],
      })
    }
    // Increment quantity_received on registry item
    const recvQty = item.reserved_qty || 1
    const newRecv = (item.quantity_received||0) + recvQty
    const updateData = { quantity_received: newRecv }
    if (newRecv >= (item.quantity_needed||1)) {
      updateData.status = 'received'
    }
    await supabase.from('registry_items').update(updateData).eq('id', item.id)
    setMarking(null)
    fetch()
  }

  async function markNotReceived(item) {
    // Remove this specific reservation
    await supabase.from('registry_reservations').delete().eq('id', item.reservation_id)
    // Decrement quantity_reserved
    const newQty = Math.max(0, (item.quantity_reserved||1) - 1)
    const newStatus = newQty === 0 ? 'available' : 'reserved'
    await supabase.from('registry_items').update({ quantity_reserved: newQty, status: newStatus }).eq('id', item.id)
    fetch()
  }

  function onSent(id) {
    // Mark as sent (don't remove — so it shows in Sent view)
    setGifts(prev => prev.map(g => g.id===id ? {...g, thank_you_sent:true} : g))
  }

  const totalGifts   = gifts.length
  const cashTotal    = gifts.filter(g=>g.gift_type==='cash').reduce((s,g)=>s+Number(g.amount||0),0)
  const pendingCount = gifts.filter(g=>!g.thank_you_sent).length
  const sentNames    = gifts.filter(g=>g.thank_you_sent).map(g=>g.from_name)

  // Pending = not sent, Sent = sent
  const visible = gifts.filter(g => {
    const byStatus = status==='All'?true : status==='Pending'?!g.thank_you_sent : g.thank_you_sent
    const byType   = typeF==='All'?true : typeF==='🎁'?g.gift_type==='physical' : g.gift_type==='cash'
    return byStatus && byType
  })

  if (!wedding) return null

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:'1.25rem' }}>

      {/* Header */}
      <div>
        <h1 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'clamp(1.3rem,3vw,1.6rem)',margin:'0 0 0.2rem' }}>Gift Log</h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'#6B6B6B',margin:0 }}>{totalGifts} gifts · {pendingCount} thank yous pending</p>
      </div>

      {/* Stats */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'0.5rem' }}>
        {[
          { label:'Total Gifts',    amount:totalGifts,                         icon:Gift,         color:'#C4826A', bg:'rgba(196,130,106,0.1)' },
          { label:'Cash Received',  amount:`${currency}${cashTotal.toLocaleString()}`, icon:DollarSign, color:'#9BAF9B', bg:'rgba(155,175,155,0.12)' },
          { label:'Thank Yous Done',amount:totalGifts-pendingCount,            icon:Check,        color:'#9BAF9B', bg:'rgba(155,175,155,0.12)' },
          { label:'Still Pending',  amount:pendingCount,                       icon:MessageCircle,color:'#C4826A', bg:'rgba(196,130,106,0.1)' },
        ].map(s=>(
          <div key={s.label} className="card" style={{ padding:'0.875rem 0.75rem',display:'flex',alignItems:'center',gap:'0.625rem' }}>
            <div style={{ width:32,height:32,borderRadius:'0.5rem',background:s.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
              <s.icon size={15} color={s.color}/>
            </div>
            <div style={{ minWidth:0 }}>
              <p style={{ fontFamily:'Gibs, serif',fontSize:'1.1rem',color:'#1A1A1A',margin:0,fontWeight:400 }}>{s.amount}</p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.62rem',color:'#6B6B6B',margin:0,fontWeight:600 }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Pending registry items */}
      {pending.length > 0 && (
        <div style={{ border:'1.5px solid rgba(196,130,106,0.2)',borderRadius:'1rem',overflow:'hidden' }}>
          <div style={{ background:'rgba(196,130,106,0.04)',padding:'0.625rem 1rem',borderBottom:'1px solid rgba(196,130,106,0.12)' }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.78rem',color:'#C4826A',margin:0 }}>⏳ Awaiting Confirmation — {pending.length} item{pending.length!==1?'s':''}</p>
          </div>
          <div style={{ padding:'0.5rem',display:'flex',flexDirection:'column',gap:'0.375rem' }}>
            {pending.map(item=>(
              <div key={item.id} style={{ background:'white',borderRadius:'0.75rem',padding:'0.625rem 0.875rem',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.5rem',flexWrap:'wrap' }}>
                <div style={{ minWidth:0 }}>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.82rem',color:'#1A1A1A',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{item.name}</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#6B6B6B',margin:0 }}>by {item.reserved_by}{item.reserved_qty>1?` (×${item.reserved_qty})`:''}</p>
                </div>
                <div style={{ display:'flex',gap:'0.375rem',flexShrink:0 }}>
                  <button onClick={()=>markReceived(item)} disabled={marking===item.reservation_id||marking===item.id} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:700,background:'rgba(155,175,155,0.12)',border:'1.5px solid rgba(155,175,155,0.3)',borderRadius:100,padding:'0.25rem 0.625rem',cursor:'pointer',color:'#7A917A',whiteSpace:'nowrap',opacity:(marking===item.reservation_id||marking===item.id)?0.5:1 }}>{(marking===item.reservation_id||marking===item.id)?'Locking that in…':'✓ Received'}</button>
                  <button onClick={()=>markNotReceived(item)} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',background:'none',border:'1px solid #E5E0D8',borderRadius:100,padding:'0.25rem 0.5rem',cursor:'pointer',color:'#BCBCBC' }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter row — status + type in one clean row */}
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.625rem',flexWrap:'wrap' }}>
        {/* Status pills */}
        <div style={{ display:'flex',background:'#F3F0EA',borderRadius:100,padding:3,gap:2 }}>
          {[{v:'All',l:'All'},{v:'Pending',l:'⏳ Pending'},{v:'Sent',l:'✅ Sent'}].map(t=>(
            <button key={t.v} onClick={()=>setStatus(t.v)} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:status===t.v?700:500,padding:'0.3rem 0.75rem',borderRadius:100,border:'none',cursor:'pointer',background:status===t.v?'white':'transparent',color:status===t.v?'#1A1A1A':'#6B6B6B',whiteSpace:'nowrap' }}>{t.l}</button>
          ))}
        </div>
        {/* Type icon chips */}
        <div style={{ display:'flex',gap:'0.25rem' }}>
          {[{v:'All',l:'All'},{v:'🎁',l:'🎁'},{v:'💵',l:'💵'}].map(t=>(
            <button key={t.v} onClick={()=>setTypeF(t.v)} title={t.v==='All'?'All types':t.v==='🎁'?'Physical':'Cash'}
              style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:t.v==='All'?'0.72rem':'1rem',fontWeight:typeF===t.v?700:400,width:t.v==='All'?'auto':32,height:32,padding:t.v==='All'?'0 0.5rem':0,borderRadius:100,border:`1.5px solid ${typeF===t.v?'#C4826A':'#E5E0D8'}`,cursor:'pointer',background:typeF===t.v?'rgba(196,130,106,0.08)':'white',color:typeF===t.v?'#C4826A':'#6B6B6B',lineHeight:1 }}>{t.l}</button>
          ))}
        </div>
      </div>

      {/* Sent view — compact name chips */}
      {status==='Sent' && sentNames.length>0 && (
        <div style={{ background:'rgba(155,175,155,0.06)',border:'1px solid rgba(155,175,155,0.2)',borderRadius:'1rem',padding:'1rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.72rem',color:'#9BAF9B',textTransform:'uppercase',letterSpacing:'0.06em',margin:'0 0 0.625rem' }}>Thanked</p>
          <div style={{ display:'flex',flexWrap:'wrap',gap:'0.375rem' }}>
            {sentNames.map((name,i)=>(
              <span key={i} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:600,color:'#7A917A',background:'white',border:'1px solid rgba(155,175,155,0.3)',borderRadius:100,padding:'0.25rem 0.625rem' }}>✓ {name}</span>
            ))}
          </div>
        </div>
      )}

      {/* Gift list */}
      {loading ? (
        <p style={{ fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',textAlign:'center',padding:'2rem' }}>Just a second…</p>
      ) : status==='Sent' ? null : visible.length===0 ? (
        <div style={{ textAlign:'center',padding:'2.5rem 1rem' }}>
          <Gift size={34} color="#E5E0D8" style={{ marginBottom:'0.875rem' }}/>
          <h3 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1rem',margin:'0 0 0.3rem' }}>{gifts.length===0?'No gift should go without a thank you':'Nothing here'}</h3>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'#6B6B6B',lineHeight:1.65 }}>{gifts.length===0?'As gifts arrive, mark them received from the registry section above.':'All thank yous sent! 🎉'}</p>
        </div>
      ) : (
        <div style={{ display:'flex',flexDirection:'column',gap:'0.5rem' }}>
          {visible.map(gift=>(
            <div key={gift.id} className="card" style={{ padding:'0.875rem 1rem' }}>
              <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'0.5rem' }}>
                <div style={{ minWidth:0,flex:1 }}>
                  <div style={{ display:'flex',alignItems:'center',gap:'0.4rem',marginBottom:'0.15rem' }}>
                    <span style={{ fontSize:'0.875rem',flexShrink:0 }}>{gift.gift_type==='cash'?'💵':gift.gift_type==='group'?'👥':'🎁'}</span>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem',color:'#1A1A1A',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{gift.description}</p>
                  </div>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:0 }}>
                    From: <strong>{gift.from_name}</strong>
                    {gift.amount>0 && <span> · {currency}{Number(gift.amount).toLocaleString()}</span>}
                    {gift.received_at && <span> · {new Date(gift.received_at+'T00:00:00').toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</span>}
                  </p>
                </div>
                <button onClick={()=>setThankYou(gift)}
                  style={{ display:'flex',alignItems:'center',gap:'0.3rem',background:'rgba(196,130,106,0.07)',border:'1px solid rgba(196,130,106,0.2)',borderRadius:100,padding:'0.3rem 0.75rem',cursor:'pointer',flexShrink:0 }}>
                  <MessageCircle size={11} color="#C4826A"/>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:700,color:'#C4826A',whiteSpace:'nowrap' }}>Thank You</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {thankYou && <ThankYouModal gift={thankYou} couple={couple} onClose={()=>setThankYou(null)} onSent={onSent}/>}
    </div>
  )
}
